// The read-only block of locked Accelerator work that Momentum tools are built on.
//
// Lifted out of ChatPanel so more than one surface can build it: the module chat
// pane and the Offer Ladder's drafting chat both need the same context, assembled
// the same way, and a second hand-rolled copy would drift.
//
// This is injected into the API call only — never stored in state or Supabase.

/* Module key → the heading Brill sees. Deliberately her language, not ours.
   EXPORTED because the context CHIPS read it too: a chip is a claim about what
   Brill can see, and a claim written out by hand in a second place is a claim
   that drifts the first time a scope changes. It did — My Story reached Brill
   on an About page and the row said nothing about it. */
export const MODULE_LABELS = {
  vision: "My Vision",
  mission: "My Mission",
  client: "My Client",
  story: "My Story",
  market: "My Market",
  offer: "My Offer",
  voice: "My Voice & Branding",
  positioning: "My Positioning",
  website: "My Website",
  social: "My Visibility",
};

/**
 * Which sections each Momentum tool actually needs, as `{ moduleKey: [section] }`.
 *
 * Everything a coach has locked is a lot of text, and it rides along on *every*
 * message in a conversation. A tool that names what it needs pays for that much
 * and no more. Omit a tool here and it gets the whole locked record, which is the
 * existing behaviour and still the right default for broad tools like the Website
 * studio.
 *
 * Section names must match `MODULE_PROMPTS` in constants/modules.js exactly — they
 * are the keys outputs are stored under. Verified against it 2026-08-06:
 * Secret Sauce and WORTHI Category live in `offer`; REAL Market Check lives in
 * `client`, not `offer`.
 */
export const CONTEXT_SCOPES = {
  /* ASK BRILL'S COACHES (2026-10-22). Each takes what its question needs, not
     the whole record — a full foundation is ~32-42k tokens and rides along on
     every message (measured 2026-10-22). A section she has not finished is
     simply absent, and the coach is told to say so. */
  chat_mindset: {
    vision: ["Me", "Family", "Community", "Money", "Core Values", "Mantra"],
    mission: ["The Transformation Promise"],
    story: ["My Archetype & My Personal Style"],
  },
  chat_story: {
    /* Not her whole founder arc: this coach is for the stories she has NOT
       told yet. The turning points are enough to keep a new one consistent. */
    story: ["When the Chapter Closed", "The Path You Chose", "My Archetype & My Personal Style"],
    mission: ["The One Problem", "Who You're For"],
    client: ["Ideal Client Profile", "Tension & Transformation"],
    offer: ["Your Signature Offer"],
    voice: ["Voice Profile"],
  },
  chat_business: {
    vision: ["Me", "Family", "Money", "Business Targets", "Core Values"],
    mission: ["Niche Statement"],
    offer: ["Your Method", "WORTHI Category", "Your Signature Offer", "Your Curriculum", "Pricing"],
    social: ["Sales Path"],
    story: ["My Archetype & My Personal Style"],
  },
  chat_sales: {
    client: ["Ideal Client Profile", "Tension & Transformation", "REAL Market Check"],
    offer: ["Secret Sauce", "WORTHI Category", "Your Signature Offer", "Pricing"],
    positioning: ["The One Line", "Elevator Pitch", "Pitch Framing"],
    market: ["Validation Summary & 1:1 Prep"],
    social: ["Sales Path"],
  },
  chat_socialmedia: {
    social: ["Channel Selection", "Visibility Strategy", "Content Pillars"],
    voice: ["Voice Profile"],
    positioning: ["The One Line", "Sound Bites"],
    client: ["Ideal Client Profile"],
    offer: ["Your Signature Offer"],
  },
  offer_ladder: {
    client: ["Ideal Client Profile", "Tension & Transformation", "REAL Market Check"],
    offer: ["Secret Sauce", "Your Method", "WORTHI Category", "Your Signature Offer", "Pricing"],
    voice: ["Voice Profile"],
  },
  /* Drafting one section of one website page. Website copy is a VOICE and
     POSITIONING job before it's an offer job — the words have to sound like her
     and land the same promise the rest of her site makes — so the scope leans
     that way rather than carrying her whole locked record.
     Hero Statement is included because every page is written under it; the four
     page sections are NOT, because the page's own copy is already in the section
     she's editing and sending it twice would just cost money. */
  website_copy: {
    /* HER MISSION, added 2026-09-15 on her call: "I think my mission needs to be
       in website." It was absent entirely, which left the whole site written
       without the one thing that says WHO IT IS FOR — so a rule saying "let this
       filter every word choice on every page" had nothing to filter against.

       Three sections, and they are a PAIR PLUS A FILTER, in her words:
       `The One Problem` and `Who You're For` are the pain — what hurts and who
       it hurts — and `The Transformation Promise` is the after. A page written
       from the pain alone is a diagnosis; a page written from the promise alone
       is a claim. A banner needs both in one breath, and so does a services
       section.

       The Transformation Promise is NOT a duplicate of Tension & Transformation,
       which was my first reading and was wrong (hers, 2026-09-15): that one is
       her client's felt journey, this one is the promise her BUSINESS makes.
       Different sentences, different jobs.

       The last two stay out. The Niche Statement says what The One Line already
       says here, and the Expanded Mission Statement is long and internal. Every
       section rides along on every message of the conversation. */
    mission: ["The One Problem", "Who You're For", "The Transformation Promise"],
    client: ["Ideal Client Profile", "Tension & Transformation"],
    offer: ["WORTHI Category", "Your Signature Offer", "Pricing"],
    voice: ["Voice Profile", "Brand Feeling & Color Palette"],
    positioning: ["The One Line", "Positioning Statements"],
    website: ["Hero Statement"],
  },
  /* Building one block of one course module. This is a DELIVERY job, not a
     marketing one: what she teaches, to whom, in her voice. Her Method is the
     thing being taught, and Your Curriculum carries the arc the module sits in —
     what came before it and what it sets up — which is what stops each module
     being designed as if it were the only one.
     Positioning and website copy are deliberately absent: nothing here is
     audience-facing marketing, and every extra section rides along on every
     message of the conversation. */
  course_copy: {
    client: ["Ideal Client Profile", "Tension & Transformation"],
    offer: ["Your Method", "Your Signature Offer", "Your Curriculum"],
    voice: ["Voice Profile"],
  },
  /* Writing one touch of an outreach sequence (2026-09-16). A short message to
     one person: WHO she is writing to and how she sounds do almost all the
     work, so the scope is the client, her voice, who she is for, and the one
     line that says what she does. Her offer rides in on the touch itself
     ([TOUCH_CONTEXT] names the connected offer from her ladder), so the locked
     Signature Offer is not sent on every message. */
  outreach_copy: {
    mission: ["Who You're For"],
    client: ["Ideal Client Profile", "Tension & Transformation"],
    voice: ["Voice Profile"],
    positioning: ["The One Line"],
  },
  /* Writing a call script (2026-09-16). What she says to one person about
     their situation and her offer: the client and the tension she solves, the
     method and offer the call walks toward, and her voice — which matters MORE
     spoken than written. The connected offer from her ladder also rides in on
     [CALL_CONTEXT]. Positioning and the curriculum stay out. */
  /* Writing a presentation (2026-09-16): who the room is, what she teaches and
     sells, and how she sounds. The curriculum rides along for the Weekly
     Teaching Deck. Story stays out until the Signature Talk type exists. */
  presentation_copy: {
    client: ["Ideal Client Profile", "Tension & Transformation"],
    offer: ["Your Method", "Your Signature Offer", "Your Curriculum"],
    voice: ["Voice Profile"],
  },
  call_script_copy: {
    client: ["Ideal Client Profile", "Tension & Transformation"],
    offer: ["Your Method", "Your Signature Offer"],
    voice: ["Voice Profile"],
  },
  /* A VIDEO script reads the same locked sections as a call script — who it is
     for, what changes, her method, her offer, her voice. What differs is the
     GUIDE (api/_lib/prompts.js video_script_copy), because talking to a lens is not
     a conversation. */
  video_script_copy: {
    client: ["Ideal Client Profile", "Tension & Transformation"],
    offer: ["Your Method", "Your Signature Offer"],
    voice: ["Voice Profile"],
  },

  /* A NEWSLETTER piece (2026-09-17). The widest Grow scope, and deliberately:
     it is long-form writing in her voice that has to make a claim and point at
     an offer. CONTENT PILLARS is the one thing here that no other drafting tool
     reads — every blog post reinforces one of her three (her requirement), and
     the pillars are HER words from My Visibility, which is also where the
     Social Generator reads them from (utils/pillars.js). Positioning is in
     because a claim someone could quote IS her positioning said plainly. Her
     Curriculum stays out: a post is not where she teaches the method. */
  newsletter_copy: {
    client: ["Ideal Client Profile", "Tension & Transformation"],
    offer: ["Your Signature Offer"],
    voice: ["Voice Profile"],
    positioning: ["The One Line", "Positioning Statements"],
    social: ["Content Pillars"],
    story: ["My Archetype & My Personal Style"],
  },

  /* ── THE SIX MOMENTUM MODULE CHATS ───────────────────────────────────────
     Added 2026-08-26. Until now every one of these was absent from this map,
     so each fell through to `Object.keys(outputs)` and received her ENTIRE
     locked record on EVERY message — around 15,000 tokens for a full client,
     re-sent every turn. Momentum is the only place in the product where running
     cost actually matters, and this was the largest single source of it.

     It was also carrying rubbish. A coach with sections from a retired module
     structure — `vision / Welcome`, `vision / Current State` — shipped those
     too, because "everything" means everything, including keys nothing can
     display any more.

     Same rule as the three above: name what the job needs. Section names must
     match MODULE_PROMPTS in constants/modules.js exactly. */

  /* Her whole site: hero, homepage, services, about, contact. The broadest of
     the six by nature — it is the only tool that writes about her PAST as well
     as her offer, because the About page is a story page. Even so this is ~14
     sections rather than ~35. */
  website: {
    mission: ["Niche Statement", "The Transformation Promise"],
    client: ["Ideal Client Profile", "Tension & Transformation"],
    offer: ["Your Method", "WORTHI Category", "Your Signature Offer", "Pricing"],
    voice: ["Voice Profile", "Brand Feeling & Color Palette"],
    positioning: ["The One Line", "Positioning Statements", "Elevator Pitch"],
    story: ["My Archetype & My Personal Style", "What It Cost & What You Carried"],
  },

  /* Course — the module-level chat. Deliberately identical to `course_copy`
     above, which drafts one BLOCK inside a module: same job at a different
     altitude, so the same foundation. If one of these two ever needs a section
     the other doesn't, that is worth a comment saying why. */
  lesson_plans: {
    client: ["Ideal Client Profile", "Tension & Transformation"],
    offer: ["Your Method", "Your Signature Offer", "Your Curriculum"],
    voice: ["Voice Profile"],
  },

  /* Content Plan — signature posts, engagement rhythm, calendar. A planning job
     built on her pillars and her voice. `Your Signature Offer` is here because
     content that never points at anything is a hobby; her Method is not,
     because a post is not where she teaches it. */
  content_studio: {
    client: ["Ideal Client Profile", "Tension & Transformation"],
    offer: ["Your Signature Offer"],
    voice: ["Voice Profile", "Brand Feeling & Color Palette"],
    positioning: ["The One Line", "Positioning Statements", "Elevator Pitch"],
    social: ["Channel Selection", "Visibility Strategy", "Content Pillars"],
    story: ["My Archetype & My Personal Style"],
  },

  /* The post generator. This list is COPIED FROM `buildSocialContext` in
     App.jsx, which already decided what writing in her voice needs — plus
     Content Pillars, which that path fetches separately. Copied rather than
     re-reasoned on purpose: two lists answering "what does a social tool need"
     is exactly how they drift. If one changes, change both. */
  social_generator: {
    mission: ["Niche Statement", "The Transformation Promise", "Expanded Mission Statement"],
    client: ["Ideal Client Profile", "Tension & Transformation"],
    voice: ["Voice Profile", "Brand Feeling & Color Palette"],
    positioning: ["The One Line", "Elevator Pitch", "Positioning Statements"],
    story: ["My Archetype & My Personal Style", "What It Cost & What You Carried"],
    social: ["Content Pillars"],
  },

  /* ── The two that are still "coming soon" ──────────────────────────────
     `operations_hub` and `sales_hub` are placeholders — see
     MOMENTUM_PLACEHOLDER_MODULES — with no entry in MODULE_GUIDES, so nothing
     reaches Brill through them today.

     They are scoped anyway, and that is the point of writing them down: an
     ABSENT entry means "send her whole locked record", so a tool built later by
     someone who doesn't know this file would inherit the exact bug this change
     is fixing. A wrong scope shows up as Brill missing something obvious. A
     missing scope shows up as a bill.

     PROVISIONAL. Revise when the tools are actually built — this is a guess
     from their placeholder copy, not from a spec. */
  operations_hub: {
    client: ["Ideal Client Profile"],
    offer: ["Your Signature Offer", "Your Curriculum", "Pricing"],
    voice: ["Voice Profile"],
  },
  sales_hub: {
    client: ["Ideal Client Profile", "Tension & Transformation", "REAL Market Check"],
    offer: ["Secret Sauce", "WORTHI Category", "Your Signature Offer", "Pricing"],
    voice: ["Voice Profile"],
    positioning: ["The One Line", "Elevator Pitch"],
  },
};

/**
 * Build the context block, or null when there's nothing worth sending.
 *
 * @param {object}   o
 * @param {string}   o.moduleId            the Momentum tool asking
 * @param {object}   o.acceleratorOutputs  { [moduleKey]: { [section]: content } }
 * @param {object?}  o.activeNiche         the niche row she's currently building for
 * @param {function} o.isStale             (moduleKey, section) => boolean
 * @param {object?}  o.scope               section allowlist; defaults to CONTEXT_SCOPES[moduleId]
 * @param {object?}  o.extraScope          sections to ADD to that allowlist, in the
 *                                         same shape. For the case where one tool
 *                                         needs more depending on what it is
 *                                         pointed at: an About page is written
 *                                         from her My Story, and no other page is.
 *                                         Merged per module rather than replacing,
 *                                         so an extra never silently drops what
 *                                         the tool's own scope asked for.
 *                                         NOT a way around scoping — every extra
 *                                         section rides along on every message of
 *                                         that conversation, so it has to be worth
 *                                         it for the page it is added for.
 */
export function buildMomentumContext({ moduleId, acceleratorOutputs, activeNiche, isStale = () => false, scope, extraScope }) {
  if (!acceleratorOutputs) return null;
  const base = scope || CONTEXT_SCOPES[moduleId] || null;
  /* Only merged when there IS a base list. With no scope the tool already gets
     everything she has locked, so adding to it would be a no-op that reads as
     though it did something. */
  const allow = base && extraScope
    ? Object.entries(extraScope).reduce((acc, [id, names]) => ({
      ...acc,
      [id]: [...new Set([...(acc[id] || []), ...(names || [])])],
    }), { ...base })
    : base;

  let anyStale = false;
  const sections = Object.entries(acceleratorOutputs)
    .map(([id, outputs]) => {
      const wanted = allow ? (allow[id] || []) : Object.keys(outputs || {});
      const body = wanted
        .filter((name) => outputs?.[name])
        .map((name) => {
          /* Sections built on an earlier niche are labelled rather than dropped.
             Dropping them would leave Momentum with holes it can't see; presenting
             them unlabelled as "the locked record" is worse — it would have Brill
             write new work from a client the coach no longer serves. */
          if (isStale(id, name)) {
            anyStale = true;
            return `[${name} — BUILT ON A PREVIOUS NICHE, treat as unreliable]\n${outputs[name]}`;
          }
          return `[${name}]\n${outputs[name]}`;
        })
        .join("\n\n");
      return body ? `--- ${MODULE_LABELS[id] || id} ---\n${body}` : "";
    })
    .filter(Boolean)
    .join("\n\n");

  if (!sections) return null;

  const nicheLine = activeNiche
    ? `\n\nHer current niche is: ${activeNiche.label}${activeNiche.statement ? ` — ${activeNiche.statement}` : ""}.`
    : "";
  const staleNote = anyStale
    ? " Sections marked BUILT ON A PREVIOUS NICHE predate her current niche and she has not yet confirmed them — do not treat those as settled, and say so if you have to lean on one."
    : "";

  return `[MOMENTUM_CONTEXT — read only, do not reference as a conversation]\nThe following are this coach's confirmed locked outputs from the Business Brilliance Accelerator. Use them as the foundation for all Momentum work. Do not treat them as part of the chat — they are the locked record.${staleNote}${nicheLine}\n\n${sections}`;
}
